import { useState, useCallback } from 'react';

import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import makeStyles from '@mui/styles/makeStyles';

import SharedAsset from '#client/components/SharedAsset';
import manifest from '#client/manifest';

const useStyles = makeStyles((theme) => ({
  baseDialog: {
    backgroundColor: '#FAFAFA',
    borderRadius: 16,
    padding: '24px 24px 60px'
  },
  editDialog: {
    width: 600,
    [theme.breakpoints.down('sm')]: {
      width: 'auto',
      marginTop: 0,
      borderRadius: 0,
      padding: '20px 16px 60px'
    }
  },
  successDialog: {
    padding: 0,
    maxWidth: 400
  },
  successDialogSpacing: {
    padding: '24px 65px',
    [theme.breakpoints.up('sm')]: {
      padding: '48px 32px 32px'
    }
  },
  editDialogTitle: {
    padding: 0,
    paddingTop: 40,
    [theme.breakpoints.up('sm')]: {
      paddingTop: 36
    }
  },
  content: {
    padding: 0,
    paddingTop: 32,
    [theme.breakpoints.up('sm')]: {
      paddingTop: 48
    }
  },
  longButton: {
    marginBottom: 48,
    padding: '16px 24px',
    margin: 'auto',
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  },
  longSection: {
    marginBottom: 24,
    [theme.breakpoints.up('sm')]: {
      textAlign: 'center'
    }
  },
  longerSection: {
    marginTop: 32,
    marginBottom: 32
  },
  section: {
    '& + &': {
      marginTop: 32,
      [theme.breakpoints.up('sm')]: {
        marginTop: 40
      }
    },
    [theme.breakpoints.up('sm')]: {
      textAlign: 'center',
      maxWidth: 392,
      margin: 'auto'
    }
  },
  sectionTitle: {
    ...theme.typography.body_largeRegular,
    [theme.breakpoints.up('sm')]: {
      ...theme.typography.body_extraLargeRegular
    }
  },
  sectionBody: {
    ...theme.typography.body_smallRegular,
    [theme.breakpoints.up('sm')]: {
      ...theme.typography.body_largeRegular
    }
  },
  closeButton: {
    position: 'absolute',
    top: 12,
    right: 10,
    [theme.breakpoints.up('sm')]: {
      top: 24,
      right: 24
    }
  },
  modalHeader: {
    ...theme.typography.title_medium,
    [theme.breakpoints.up('sm')]: {
      ...theme.typography.title_large,
      textAlign: 'center'
    }
  }
}));

const EditModal = ({ isOpen, onClose, account }) => {
  const { resetPassword } = useAuthentication();
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [exception, setException] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();

    try {
      await resetPassword(account.accountEmail);
      setSuccess(true);
      return;
    } catch (err) {
      setException(err);
    }
  }, [account.accountEmail, resetPassword]);

  if (exception) throw exception;

  if (success) {
    return (
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth={false}
        PaperProps={{
          className: [classes.baseDialog, classes.successDialog]
        }}
        data-testid="edit-success"
      >
        <DialogTitle className={classes.successDialogSpacing}>
          <Grid
            container
            direction="column"
            justifyContent="space-between"
            alignItems="center"
          >
            <SharedAsset src="/icon/product/basic-email-256.svg" width={80} alt="" align="center" />
            <Typography variant="h2" component="p" align="center" className={classNames(classes.longerSection, classes.modalHeader)} data-testid="sent-email">
              We&apos;ve sent you a password reset&nbsp;email
            </Typography>
            <Button
              size="medium"
              variant="text"
              color="primary"
              onClick={onClose}
            >
              Close
            </Button>
          </Grid>
        </DialogTitle>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullScreen={fullScreen}
      PaperProps={{
        className: [classes.baseDialog, classes.editDialog]
      }}
      data-testid="edit-account"
    >
      <DialogTitle component="div" className={classes.editDialogTitle}>
        <Typography
          variant="h2"
          component="p"
          className={classes.modalHeader}
        >
          Edit Account Settings
        </Typography>
        <Button
          variant="text"
          color="primary"
          onClick={onClose}
          className={classes.closeButton}
        >
          Close
        </Button>
      </DialogTitle>

      <DialogContent className={classes.content}>
        <Grid
          container
          direction="column"
          justifyContent="center"
        >
          <Grid container item direction="column" className={classes.section}>
            <Typography variant="body2" className={classes.longSection}>
              We&apos;ll email you a link to reset your password.
            </Typography>
            <Button
              color="primary"
              variant="contained"
              type="submit"
              onClick={handleSubmit}
              className={classes.longButton}
            >
              Reset Your Password
            </Button>
            <Typography variant="body2">
              To change other account information, contact us through phone, email, or chat.
            </Typography>
          </Grid>

          <Grid container item direction="column" spacing={1} className={classes.section}>
            <Grid item>
              <Typography variant="h4" component="h3" className={classes.sectionTitle}>
                Call Us
              </Typography>
            </Grid>
            <Grid item>
              <Link href="tel:844-493-6249" target="_blank" rel="noopener" variant="h6" className={classes.sectionTitle}>1-844-4-ZENBIZ</Link>
            </Grid>
            <Grid item>
              <Typography variant="body2" className={classes.sectionBody}>
                {`You can call our team during business hours ${manifest.contactHours}.`}
              </Typography>
            </Grid>
          </Grid>

          <Grid container item direction="column" spacing={1} className={classes.section}>
            <Grid item>
              <Typography variant="h4" component="h3" className={classes.sectionTitle}>
                Send Us an Email
              </Typography>
            </Grid>
            <Grid item>
              <Link href="mailto:support@zenbusiness.com" target="_blank" rel="noopener" variant="h6" className={classes.sectionTitle}>support@zenbusiness.com</Link>
            </Grid>
            <Grid item>
              <Typography variant="body2" className={classes.sectionBody}>
                Send an email to our customer success team and we will reply within 1
                business day.
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

EditModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  account: PropTypes.shape({
    accountEmail: PropTypes.string
  }).isRequired
};

export default EditModal;
